import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  onAuthStateChanged
} from 'firebase/auth';
import { 
  auth, 
  signInWithGoogle as firebaseSignInWithGoogle,
  signInWithMicrosoft as firebaseSignInWithMicrosoft,
  logOut,
  createOrUpdateUserDocument
} from './firebase';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<User>;
  signInWithMicrosoft: () => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        console.log('Auth state changed: User logged in', user.email);
        // Create or update the user document in Firestore
        try {
          await createOrUpdateUserDocument(user);
        } catch (error) {
          console.error('Error updating user document on auth state change:', error);
        }
      } else {
        console.log('Auth state changed: No user');
      }
      
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    return await firebaseSignInWithGoogle();
  };

  const signInWithMicrosoft = async () => {
    return await firebaseSignInWithMicrosoft();
  };

  const logout = async () => {
    await logOut();
  };

  const value = {
    currentUser,
    loading,
    signInWithGoogle,
    signInWithMicrosoft,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 